<script setup lang="ts">
import { watch } from 'vue'
import { useMagicKeys } from '@vueuse/core'

// Refs
const userEmail = ref<string>('')
const menu = ref<boolean>(false)
const { t } = useNuxtApp().$i18n
const route = useRoute()
const userRoles = ref<string[]>([])
const userPermissions = ref<Record<string, boolean>>({})
const isAdmin = computed(() => userRoles.value.includes('Admin'))

// Generate appropriate route paths - always use POS routes now
const getStorePath = (page: string = '') => {
  return page ? `/store/pos/${page}` : '/store/pos'
}

// Check if a link is active
const isActiveLink = (path: string) => {
  return route.path === path
}

// Composables
const user = useSupabaseUser()
const sbAccessToken = useCookie('sb-access-token')
const { get } = useAuthenticatedFetch()

// Commerce store
const commerceStore = useCommerceStore()
const { combos } = storeToRefs(commerceStore)

// Business store
const businessStore = useBusinessStore()
const { businessInfo, businessId } = storeToRefs(businessStore)

// Check if there are combos and feature is enabled
const hasCombos = computed(() =>
  combos.value
  && combos.value.length > 0
  && businessInfo.value?.pos_config?.enable_combos === true,
)

// Search modal state
const searchModal = ref<boolean>(false)

const navigation = computed(() => {
  // Always use POS routes now - deprecated [buuid] pattern
  const items: Array<{ href: string, name: string, icon: string, action?: string }> = [
    { href: getStorePath(), name: 'Point of sale', icon: 'carbon:sales-ops' },
    // Counter is "not restaurant" everywhere else in the POS (layout, cart);
    // a strict === 'counter' hid these for businesses with service_type null.
    ...(businessInfo.value?.service_type !== 'restaurant'
      ? [{ href: getStorePath('orders'), name: 'Orders', icon: 'carbon:collapse-categories' }]
      : []),
    // Agenda is switched on per business from super-admin (business.features).
    ...(businessInfo.value?.service_type !== 'restaurant' && businessInfo.value?.features?.bookings
      ? [{ href: getStorePath('bookings'), name: 'Bookings', icon: 'carbon:calendar' }]
      : []),
    ...(businessInfo.value?.service_type !== 'restaurant' && businessInfo.value?.features?.appointments
      ? [{ href: getStorePath('appointments'), name: 'appointment.title', icon: 'carbon:event-schedule' }]
      : []),
    ...(businessInfo.value?.service_type === 'restaurant' && businessInfo.value?.pos_config?.enable_kds !== false
      ? [{ href: getStorePath('kds'), name: 'KDS', icon: 'carbon:grid' }]
      : []),
  ]

  // Inventory link - visible to admins and employees with can_view_stock
  if (isAdmin.value || userPermissions.value.can_view_stock) {
    items.push({ href: getStorePath('inventory'), name: 'Inventory', icon: 'carbon:inventory-management' })
  }

  // Admin-only links
  if (isAdmin.value) {
    // Determine dashboard path based on service type
    const dashboardPath = businessInfo.value?.service_type === 'restaurant' ? '/app/checks' : '/app/orders'

    items.push(
      { href: dashboardPath, name: 'Dashboard', icon: 'carbon:dashboard-reference' },
      { href: '/app/business', name: 'Business', icon: 'clarity:details-line' },
      { href: '/app/reports', name: 'Reports', icon: 'carbon:analytics' },
    )
  }

  // Add logout if user is authenticated
  if (userEmail.value) {
    items.push({ href: '#', name: 'Log out', icon: 'carbon:logout', action: 'logout' })
  }

  return items
})

// Methods
const toggle = () => {
  menu.value = !menu.value
}

const closeMenu = () => {
  menu.value = false
}

// Side effects
watch(sbAccessToken, async () => {
  userEmail.value = user?.value?.email || ''

  // Fetch user roles when authenticated
  if (userEmail.value) {
    try {
      const response = await get('/api/user')
      if (response?.user?.users_roles) {
        userRoles.value = response.user.users_roles.map((ur: any) => ur.role)
        userPermissions.value = response.user.users_roles[0]?.permissions ?? {}
      }
    }
    catch (error) {
      console.error('Failed to fetch user roles:', error)
    }
  }
  else {
    userRoles.value = []
  }
}, { immediate: true })

// Keyboard shortcut for search modal (Cmd+K / Ctrl+K)
const { Meta_K, Ctrl_K, Escape } = useMagicKeys({
  passive: false,
  onEventFired(e) {
    // Prevent default browser behavior for Cmd+K / Ctrl+K
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault()
    }
  },
})

watch([Meta_K, Ctrl_K], ([metaK, ctrlK]) => {
  if (metaK || ctrlK) {
    searchModal.value = true
  }
})

// Double ESC to clear search
const lastEscapeTime = ref<number>(0)
const DOUBLE_ESC_THRESHOLD = 500 // 500ms window for double tap

watch(Escape, (pressed) => {
  if (pressed) {
    const now = Date.now()
    const timeSinceLastEsc = now - lastEscapeTime.value

    if (timeSinceLastEsc < DOUBLE_ESC_THRESHOLD) {
      // Double ESC detected - clear search
      commerceStore.clearSearch()
      lastEscapeTime.value = 0 // Reset
    }
    else {
      // First ESC press
      lastEscapeTime.value = now
    }
  }
})

// Global typing to filter products
useGlobalSearch(searchModal)
</script>

<template>
  <div>
    <!-- Mobile Menu -->
    <POSMobileMenu
      v-model:visible="menu"
      :navigation="navigation"
      @close="closeMenu"
    />

    <div
      v-if="businessId && userEmail"
      class="flex items-stretch gap-1 border-b border-gray-200 bg-gray-900 p-1 text-xs sm:px-6 print:hidden"
    >
      <!-- Mobile Menu Button -->
      <a
        class="ml-2 h-auto flex items-center cursor-pointer z-10"
        @click.prevent="toggle"
      >
        <i class="pi pi-bars text-lg text-white" />
      </a>
      <div class="flex-grow flex justify-start gap-1" />
      <PublicStoreShiftWrapper />
      <PublicStoreOrderUserData v-if="businessInfo?.service_type !== 'restaurant'" />
    </div>
    <div
      v-if="businessId && !userEmail"
      class="align-items-stretch justify-content-center sm:justify-content-end border-bottom-1 surface-border flex bg-gray-900 p-3 text-sm sm:px-6 print:hidden"
    >
      <NuxtLink
        to="/auth/login"
        class="align-items-center p-ripple inline-flex cursor-pointer px-3 font-medium text-white no-underline hover:text-gray-200"
        no-prefetch
      >
        <i class="pi pi-sign-in mr-2 text-sm sm:mr-3" />
        <span>Sign in</span>
      </NuxtLink>
    </div>
    <div
      class="surface-overlay align-items-stretch relative flex min-h-[50px] bg-white px-3 sm:px-6 print:hidden print:min-h-[50px] print:justify-center print:p-0"
    >
      <div
        id="main-navigation"
        class="flex items-center print:hidden"
      >
        <ul class="m-0 list-none flex gap-3 md:gap-5 p-0 pl-2">
          <!-- <li>
            <NuxtLink
              :to="getStorePath()"
              class="no-underline text-color :hover:text-color-emphasis
                flex items-center justify-center min-w-[44px] min-h-[44px]
              "
            >
              <i class="pi pi-home text-gray-800 mr-0 sm:mr-2" />
              <span class="hidden sm:inline">{{ t('Start') }}</span>
            </NuxtLink>
          </li> -->
          <li>
            <NuxtLink
              :to="getStorePath()"
              :class="[
                'no-underline flex items-center justify-center min-w-[44px] min-h-[44px] rounded transition-colors px-2',
                isActiveLink(getStorePath())
                  ? 'bg-gray-200 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-100',
              ]"
            >
              <i class="pi pi-shopping-cart text-gray-800 mr-0 sm:mr-2" />
              <span class="hidden sm:inline">{{ t('Products') }}</span>
            </NuxtLink>
          </li>
          <li v-if="hasCombos">
            <NuxtLink
              :to="getStorePath('combos')"
              :class="[
                'no-underline flex items-center justify-center min-w-[44px] min-h-[44px] rounded transition-colors px-2',
                isActiveLink(getStorePath('combos'))
                  ? 'bg-gray-200 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-100',
              ]"
            >
              <i class="pi pi-th-large text-gray-800 mr-0 sm:mr-2" />
              <span class="hidden sm:inline">{{ t('Combos') }}</span>
            </NuxtLink>
          </li>
          <li v-if="businessInfo?.service_type !== 'restaurant'">
            <NuxtLink
              :to="getStorePath('orders')"
              :class="[
                'no-underline flex items-center justify-center min-w-[44px] min-h-[44px] rounded transition-colors px-2',
                isActiveLink(getStorePath('orders'))
                  ? 'bg-gray-200 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-100',
              ]"
            >
              <i class="pi pi-list text-gray-800 mr-0 sm:mr-2" />
              <span class="hidden sm:inline">{{ t('Orders') }}</span>
            </NuxtLink>
          </li>
          <li v-if="businessInfo?.service_type !== 'restaurant' && businessInfo?.features?.bookings">
            <NuxtLink
              :to="getStorePath('bookings')"
              :class="[
                'no-underline flex items-center justify-center min-w-[44px] min-h-[44px] rounded transition-colors px-2',
                isActiveLink(getStorePath('bookings'))
                  ? 'bg-gray-200 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-100',
              ]"
            >
              <i class="pi pi-calendar text-gray-800 mr-0 sm:mr-2" />
              <span class="hidden sm:inline">{{ t('Bookings') }}</span>
            </NuxtLink>
          </li>
          <li v-if="businessInfo?.service_type !== 'restaurant' && businessInfo?.features?.appointments">
            <NuxtLink
              :to="getStorePath('appointments')"
              :class="[
                'no-underline flex items-center justify-center min-w-[44px] min-h-[44px] rounded transition-colors px-2',
                isActiveLink(getStorePath('appointments'))
                  ? 'bg-gray-200 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-100',
              ]"
            >
              <i class="pi pi-clock text-gray-800 mr-0 sm:mr-2" />
              <span class="hidden sm:inline">{{ t('appointment.title') }}</span>
            </NuxtLink>
          </li>
          <li v-if="businessInfo?.service_type === 'restaurant' && businessInfo?.pos_config?.enable_kds !== false">
            <NuxtLink
              :to="getStorePath('kds')"
              :class="[
                'no-underline flex items-center justify-center min-w-[44px] min-h-[44px] rounded transition-colors px-2',
                isActiveLink(getStorePath('kds'))
                  ? 'bg-gray-200 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-100',
              ]"
            >
              <i class="pi pi-tablet text-gray-800 mr-0 sm:mr-2" />
              <span class="hidden sm:inline">{{ t('KDS') }}</span>
            </NuxtLink>
          </li>
          <li>
            <button
              class="no-underline text-color :hover:text-color-emphasis flex items-center justify-center min-w-[44px] min-h-[44px] cursor-pointer bg-transparent border-0 text-base"
              @click="searchModal = true"
            >
              <i class="pi pi-search text-gray-800 mr-0 sm:mr-2" />
              <span class="hidden sm:inline">{{ t('Search') }}</span>
            </button>
          </li>
        </ul>
      </div>
      <div class="ml-auto flex print:hidden">
        <CartSimple />
        <CartHeader />
      </div>
    </div>

    <!-- Search Modal -->
    <PublicStoreSearchModal
      v-model:visible="searchModal"
    />
  </div>
</template>
